// Tiny SSE-over-POST client for coach_api JWT-protected endpoints.
//
// `fetchApi` from ~vue/api handles token acquisition and URL resolution but
// returns a Response — we just need to read its body as an SSE stream. SSE
// frames are delimited by "\n\n"; each frame may have an `event:` line
// followed by a `data:` line. We forward parsed events to onEvent.

import { fetchApi } from "~vue/api";

export interface SseEvent {
    event: string;
    data: unknown;
}

export interface StreamOptions {
    onEvent: (event: SseEvent) => void;
    signal?: AbortSignal;
}

const parseFrame = (frame: string): SseEvent | null => {
    if (!frame.trim() || frame.startsWith(":")) {
        return null;
    }
    let eventName = "message";
    const dataLines: string[] = [];
    for (const line of frame.split("\n")) {
        if (line.startsWith("event: ")) {
            eventName = line.slice("event: ".length).trim();
        } else if (line.startsWith("data: ")) {
            dataLines.push(line.slice("data: ".length));
        }
    }
    if (dataLines.length === 0) {
        return null;
    }
    const raw = dataLines.join("\n");
    let data: unknown = raw;
    try {
        data = JSON.parse(raw);
    } catch {
        // leave as string
    }
    return { event: eventName, data };
};

export const streamCoachApiPost = async (path: string, body: unknown, opts: StreamOptions): Promise<void> => {
    const response = await fetchApi(path, {
        method: "POST",
        body: JSON.stringify(body),
        signal: opts.signal,
    });

    if (!response.ok || !response.body) {
        const text = await response.text().catch(() => response.statusText);
        throw new Error(`SSE request failed: HTTP ${response.status} ${text}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            const frames = buffer.split("\n\n");
            buffer = frames.pop() ?? "";
            for (const frame of frames) {
                const parsed = parseFrame(frame);
                if (parsed) {
                    opts.onEvent(parsed);
                }
            }
        }
        if (buffer.trim()) {
            const parsed = parseFrame(buffer);
            if (parsed) {
                opts.onEvent(parsed);
            }
        }
    } finally {
        reader.releaseLock();
    }
};
